import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiButtonEmpty, EuiCallOut, EuiFieldText, EuiFormRow, EuiImage, EuiModal, EuiModalBody, EuiModalFooter, EuiModalHeader, EuiModalHeaderTitle, EuiSpacer } from '@elastic/eui'

import { Creative } from 'api/interfaces'
import { useUpdateDisplayAdMutation } from 'api/rtkQueryApi/platform/creativesApi'
import { showErrorToast, showSuccessToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'
import { AcImageUploader, UploadedImage } from 'components/Basic/AcImageUploader'

interface EditDisplayAdModalProps {
  campaignId: string
  creative: Creative
  onClose: () => void
}

const imageMimeType = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase() ?? ''
  return `image/${extension === 'jpg' ? 'jpeg' : extension}`
}

export const EditDisplayAdModal: React.FC<EditDisplayAdModalProps> = ({ campaignId, creative, onClose }) => {
  const dispatch = useDispatch()
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [name, setName] = useState(creative.name ?? '')
  const [destinationUrl, setDestinationUrl] = useState(creative.destinationUrl ?? '')
  const [replacementImage, setReplacementImage] = useState<UploadedImage | null>(null)
  const [isImageValid, setIsImageValid] = useState(true)
  const [updateDisplayAd, updateDisplayAdResult] = useUpdateDisplayAdMutation()

  const previewUrl = replacementImage?.url ?? creative.displayAdDetails?.uploadedUrl ?? creative.thumbnailUrl
  const canSave = destinationUrl.trim().length > 0 && isImageValid

  const onImagesUploaded = (images: UploadedImage[]) => {
    setReplacementImage(images.length > 0 ? images[0] : null)
  }

  const onSave = async () => {
    if (!currentAccount) {
      return
    }

    try {
      await updateDisplayAd({
        accountId: currentAccount.id,
        campaignId,
        creativeId: creative.id,
        name,
        destinationUrl: destinationUrl.trim(),
        uploadedUrl: replacementImage?.url,
        width: replacementImage?.width,
        height: replacementImage?.height,
        mimeType: replacementImage ? imageMimeType(replacementImage.fileName) : undefined
      }).unwrap()
      dispatch(showSuccessToast('Updated ad'))
      onClose()
    } catch {
      dispatch(showErrorToast('Failed to update ad'))
    }
  }

  return (
    <EuiModal onClose={onClose} style={{ width: 560 }}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>Edit Ad</EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiFormRow label='Name' fullWidth>
          <EuiFieldText value={name} onChange={e => setName(e.target.value)} fullWidth />
        </EuiFormRow>
        <EuiFormRow label='Destination URL' fullWidth isInvalid={destinationUrl.trim().length === 0}>
          <EuiFieldText value={destinationUrl} onChange={e => setDestinationUrl(e.target.value)} placeholder='https://' fullWidth isInvalid={destinationUrl.trim().length === 0} />
        </EuiFormRow>
        <EuiFormRow label='Image' helpText='Upload a new image to replace the current one' fullWidth>
          <React.Fragment>
            {previewUrl && <EuiImage alt='display ad' url={previewUrl} size='m' hasShadow />}
            <EuiSpacer size='s' />
            {currentAccount && <AcImageUploader accountId={currentAccount.id} allowedFileTypes={['image/png', 'image/jpeg', 'image/gif']} onUploadedUrlsChange={onImagesUploaded} onIsValidFileChange={setIsImageValid} />}
            {!isImageValid && <EuiCallOut title='Please upload a png, jpeg or gif' iconType='image' color='warning' size='s' />}
          </React.Fragment>
        </EuiFormRow>
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton fill onClick={onSave} isDisabled={!canSave} isLoading={updateDisplayAdResult.isLoading}>
          Save
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  )
}
